import * as React from 'react';
import { useState, useEffect } from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import { AppUser } from 'Src/views/App/App';
import { readStudentsFromCSVFile, pullFormsHouses } from 'Src/helpers/utils/csvUtils';
import { importStudents } from 'Src/helpers/service/admin/students';
import { deleteForm } from 'Src/helpers/service/admin/forms';
import { deleteHouse } from 'Src/helpers/service/admin/houses';
import { ImportError } from 'Src/helpers/utils/types';
import { Switch } from 'Src/components/Switch/Switch';
import './ImportStudents.scss';

interface School {
  id: string;
  name: string;
  studentImportForAdminAllowed: boolean;
}

interface FormItem {
  id: string;
  name: string;
}

interface House {
  id: string;
  name: string;
}

interface ImportStudentsProps {
  user: AppUser;
  school: School;
  forms: FormItem[];
  houses: House[];
}

const STEPS = ['Upload file', 'Map columns', 'Review', 'Done'];

const FIELD_COLUMNS = [
  ['Name', 'First name', 'firstName'],
  ['Surname', 'Surname', 'lastName'],
  ['Gender', 'Gender', 'gender'],
  ['Year', 'Age group', 'year'],
  ['Form', 'Form', 'form'],
  ['Date of birth', 'Date of birth', 'birthday'],
  ['House', 'House', 'house']
];

export const ImportStudents: React.FC<ImportStudentsProps> = ({ user, school }) => {
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [studentsToUpload, setStudentsToUpload] = useState<any[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [removeExistingStudents, setRemoveExistingStudents] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const [guessedHeaders, setGuessedHeaders] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (!school || !school.id) {
      console.error('School data is missing or incomplete.', school);
    }
  }, [school]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('sit:topbar-wizard', {
        detail: {
          steps: STEPS.map((label, index) => ({
            label,
            state: index < step ? 'done' : index === step ? 'active' : 'pending'
          }))
        }
      })
    );

    return () => {
      window.dispatchEvent(new CustomEvent('sit:topbar-wizard-clear'));
    };
  }, [step]);

  const renderCheckmark = (className?: string) => (
    <span className={className} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
  );

  const resetFile = () => {
    setFile(null);
    setStudentsToUpload([]);
    setErrors([]);
    setGuessedHeaders({});
  };

  const processFile = async (selectedFile: File) => {
    setErrors([]);

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
      setErrors([{ type: 'FileTypeError', code: 'FILE_TYPE_ERROR', message: 'Please upload a CSV file.' }]);
      resetFile();
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setErrors([{ type: 'FileSizeError', code: 'FILE_SIZE_ERROR', message: 'File size exceeds 5MB limit.' }]);
      resetFile();
      return;
    }

    setFile(selectedFile);
    setIsLoading(true);

    try {
      const result = await readStudentsFromCSVFile(selectedFile);
      setErrors(result.errors || []);
      setStudentsToUpload(result.students || []);
      setGuessedHeaders((result.meta && result.meta.guessedHeaders) || {});
    } catch (err) {
      setErrors([
        {
          type: 'FileReadError',
          code: 'FILE_READ_ERROR',
          message: err instanceof Error ? err.message : 'An unknown error occurred.'
        }
      ]);
      setFile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const handleUploadClick = async () => {
    if (!file) {
      setErrors([{ type: 'UploadError', code: 'UPLOAD_ERROR', message: 'Choose a CSV file before importing.' }]);
      return;
    }

    const schoolForUpload = school || user.activeSchool;
    if (!schoolForUpload || !(schoolForUpload.id || user.activeSchoolId)) {
      setErrors([
        {
          type: 'UploadError',
          code: 'UPLOAD_ERROR',
          message: 'Active school is not available. Please reload the page and try again.'
        }
      ]);
      return;
    }

    setIsLoading(true);
    let createdFormIds: string[] = [];
    let createdHouseIds: string[] = [];

    const rollbackCreatedStructure = async () => {
      await Promise.all([
        ...createdFormIds.map(formId => deleteForm(user, formId).catch(() => undefined)),
        ...createdHouseIds.map(houseId => deleteHouse(user, houseId).catch(() => undefined))
      ]);
    };

    try {
      const result = await readStudentsFromCSVFile(file);
      const processedResult = await pullFormsHouses(result, schoolForUpload, user);
      createdFormIds = processedResult.createdFormIds || [];
      createdHouseIds = processedResult.createdHouseIds || [];

      if (processedResult.errors.length > 0) {
        await rollbackCreatedStructure();
        setErrors(processedResult.errors);
        return;
      }

      await importStudents(user, processedResult.students, removeExistingStudents);

      setStep(3);
    } catch (error) {
      await rollbackCreatedStructure();
      setErrors(prevErrors => [
        ...prevErrors,
        {
          type: 'UploadError',
          code: 'UPLOAD_ERROR',
          message: error instanceof Error ? error.message : 'An unknown error occurred during upload.'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderUpload = () => (
    <div className="imp__panel imp__uploadgrid">
      {file ? (
        <div className="imp__file">
          <span className="imp__fileico">CSV</span>
          <div className="imp__filemeta">
            <div className="imp__filename">{file.name}</div>
            <div className="imp__filesub">
              <span>{Math.ceil(file.size / 1024)} KB</span>
              <i />
              <span>{studentsToUpload.length} rows detected</span>
            </div>
          </div>
          <button type="button" className="imp__filex" onClick={resetFile}>
            x
          </button>
        </div>
      ) : (
        <div
          className={`imp__drop ${isOver ? 'imp__drop--over' : ''}`}
          onDragOver={event => {
            event.preventDefault();
            setIsOver(true);
          }}
          onDragLeave={() => setIsOver(false)}
          onDrop={event => {
            event.preventDefault();
            setIsOver(false);
            const droppedFile = event.dataTransfer.files?.[0];
            if (droppedFile) processFile(droppedFile);
          }}
        >
          <span className="imp__dropico">CSV</span>
          <div className="imp__droptitle">Drop your file here, or browse</div>
          <div className="imp__dropsub">CSV, up to 5 MB - one student per row</div>
          <input className="imp__nativeInput" type="file" accept=".csv,text/csv" onChange={handleFileChange} />
          <div className="imp__droptypes">
            <span>.CSV</span>
          </div>
        </div>
      )}
      {errors.length > 0 ? (
        <div className="imp__errors imp__errors--upload">
          <h3>Errors detected in your file</h3>
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <aside className="imp__aside">
        <div className="imp__card">
          <h3>Start from our template</h3>
          <p>The expected columns are already set up. Fill it in, save as CSV, then drop it here.</p>
          <a
            className="imp__template"
            href="/dist/images/templates/importStudentsTemplate.csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download template
          </a>
        </div>
        <div className="imp__card">
          <h3>Columns we look for</h3>
          <div className="imp__reqlist">
            {['Name', 'Surname', 'Gender', 'Year'].map(item => (
              <span key={item} className="imp__req">
                {item}
              </span>
            ))}
            {['Form', 'Date of birth', 'House'].map(item => (
              <span key={item} className="imp__req imp__req--opt">
                {item}
              </span>
            ))}
          </div>
        </div>
        <div className="imp__card imp__danger">
          <h3>Existing students</h3>
          <p>Only use this when replacing the current student list for a new MIS import.</p>
          <div className="imp__skiprow" style={{ marginTop: 10 }}>
            <div className="imp__skiptext">
              <div className="imp__skiptitle">Replace the current student list</div>
              <div className="imp__skipsub">
                After every row passes validation, existing student roles and their linked parent roles are removed.
              </div>
            </div>
            <Switch
              name="replaceExistingStudents"
              checked={removeExistingStudents}
              onChange={event => setRemoveExistingStudents(event.target.checked)}
            />
          </div>
        </div>
      </aside>
    </div>
  );

  const getMapSample = (source: string) => {
    const student = studentsToUpload[0];
    if (!student) return '-';

    const value = (() => {
      switch (source) {
        case 'Name':
          return student.firstName || student.name;
        case 'Surname':
          return student.lastName || student.surname;
        case 'Gender':
          return student.gender;
        case 'Year':
          return student.year;
        case 'Form':
          return student.formName || student.form;
        case 'Date of birth':
          return student.birthday;
        case 'House':
          return student.houseName || student.house;
        default:
          return undefined;
      }
    })();

    return value === undefined || value === null || value === '' ? '-' : String(value);
  };

  const renderMap = () => (
    <div className="imp__panel imp__panel--map">
      <div className="imp__mapnote">
        {renderCheckmark('imp__mapnoteIcon')}
        <span>
          {FIELD_COLUMNS.filter(([, , field]) => guessedHeaders[field]).length} of {FIELD_COLUMNS.length} supported
          columns matched automatically. Check them over before moving to review.
        </span>
      </div>
      <div className="imp__mapcard">
        <div className="imp__mapheader">
          <span>CSV column</span>
          <span>Sample</span>
          <span>Squad field</span>
          <span>Status</span>
        </div>
        <div className="imp__maptable">
          {FIELD_COLUMNS.map(([source, target, field], index) => (
            <div className="imp__maprow" key={source}>
              <div className="imp__mapcol">
                <div className="imp__mapcolname">{source}</div>
                <div className="imp__maphint">Column {index + 1} in your CSV</div>
              </div>
              <div className="imp__mapsample" title={getMapSample(source)}>
                {getMapSample(source)}
              </div>
              <div className="imp__maptarget">
                <span className="imp__maparrow" aria-hidden="true">
                  -&gt;
                </span>
                <strong>{target}</strong>
              </div>
              <span className="imp__mapauto">
                <i aria-hidden="true" /> {guessedHeaders[field] ? `Matched: ${guessedHeaders[field]}` : 'Not supplied'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="imp__panel">
      <div className="imp__stats">
        <div className="imp__stat">
          {renderCheckmark('imp__statico')}
          <div>
            <div className="imp__statnum">{studentsToUpload.length}</div>
            <div className="imp__statlabel">Ready to import</div>
          </div>
        </div>
        <div className="imp__stat imp__stat--warn">
          <span className="imp__statico imp__statico--warn">!</span>
          <div>
            <div className="imp__statnum">{errors.length}</div>
            <div className="imp__statlabel">Need attention</div>
          </div>
        </div>
      </div>

      {errors.length > 0 ? (
        <div className="imp__errors">
          <h3>Errors detected in your file</h3>
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="imp__card">
        <h3>Preview</h3>
        <div className="imp__maptable">
          {studentsToUpload.slice(0, 8).map((student, index) => (
            <div className="imp__maprow" key={index}>
              <strong>
                {`${student.firstName || student.name || ''} ${student.lastName || student.surname || ''}`.trim() ||
                  `Student ${index + 1}`}
              </strong>
              <span>
                {student.gender
                  ? `${String(student.gender)
                      .charAt(0)
                      .toUpperCase()}${String(student.gender)
                      .slice(1)
                      .toLowerCase()}`
                  : '-'}
              </span>
              <span>{student.formName || student.form || student.formId || '-'}</span>
              <span className="imp__mapauto">Ready</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDone = () => (
    <div className="imp__panel imp__done">
      {renderCheckmark('imp__donecheck')}
      <div className="imp__donetitle">
        {studentsToUpload.length} student{studentsToUpload.length === 1 ? '' : 's'} imported
      </div>
      <div className="imp__donesub">They are in your student list now, sorted into forms and houses.</div>
      <div className="imp__doneactions">
        <button
          type="button"
          className="imp__btn imp__btn--secondary"
          onClick={() => {
            resetFile();
            setStep(0);
          }}
        >
          Import another file
        </button>
        <button type="button" className="imp__btn imp__btn--primary" onClick={() => window.history.back()}>
          View students
        </button>
      </div>
    </div>
  );

  if (school && !school.studentImportForAdminAllowed) {
    return (
      <div className="imp">
        <div className="imp__disabled">
          <div>
            <h2>Import students is disabled</h2>
            <p>Contact support if this school should have access to student import.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="imp">
      {isLoading ? (
        <div className="imp__loading">
          <Loader /> Processing...
        </div>
      ) : null}
      <div className="imp__head">
        <div>
          <button type="button" className="spage__back" onClick={() => window.history.back()}>
            &lt; Students
          </button>
          <h1 className="imp__title">Import students</h1>
          <p className="imp__sub">
            Add a whole year group from a spreadsheet. We check the file before anything is saved.
          </p>
        </div>
      </div>

      {step === 0 ? renderUpload() : step === 1 ? renderMap() : step === 2 ? renderReview() : renderDone()}

      {step < 3 ? (
        <div className="imp__footer">
          <div className="imp__footer-inner">
            {step === 0 ? (
              <button type="button" className="imp__btn imp__btn--secondary" onClick={() => window.history.back()}>
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="imp__btn imp__btn--secondary"
                onClick={() => setStep(prev => Math.max(0, prev - 1))}
              >
                Back
              </button>
            )}
            <span className="imp__footer-info">
              {step === 0 && file
                ? `${file.name} - ${studentsToUpload.length} rows detected`
                : step === 1
                ? `${FIELD_COLUMNS.filter(([, , field]) => guessedHeaders[field]).length} columns mapped`
                : step === 2
                ? `${studentsToUpload.length} students will be imported`
                : ''}
            </span>
            <span className="imp__footer-spacer" />
            {step === 0 ? (
              <button
                type="button"
                className="imp__btn imp__btn--primary"
                disabled={!file || errors.length > 0 || studentsToUpload.length === 0}
                onClick={() => setStep(1)}
              >
                Continue
              </button>
            ) : step === 1 ? (
              <button type="button" className="imp__btn imp__btn--primary" onClick={() => setStep(2)}>
                Review rows
              </button>
            ) : (
              <button
                type="button"
                className="imp__btn imp__btn--primary"
                disabled={errors.length > 0 || studentsToUpload.length === 0}
                onClick={handleUploadClick}
              >
                Import students
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
